// Sugar Alarm — cycle 452: when the alarm controls must follow the caregiver
// off `/now`, and which ones.
//
// THE GAP (cycle 451.1's own "Reported, not fixed"). 451.1 moved the APP to
// the controls: sustained audio pins the app to `/now`, and a one-shot cue
// takes it there once. It did not move the CONTROLS to the app, so two holes
// were left, both named:
//
//   1. The pairing family (`/pair`, `/pair-device`, `/pair-approve`) is
//      exempt from the pin by decision — breaking a handshake can strand a
//      device unpaired — so a low loop can sound there with no Snooze.
//   2. A phone urgent low is a ONE-SHOT (cycle 374). After its single
//      navigation the caregiver may leave `/now` again, and the episode is
//      still active and un-snoozed with no control under them.
//
// `CompactAlarmControl` closes both. This file decides WHEN it shows and
// WHAT it offers, as pure functions over the episode, in the style of
// `alarmAudible.ts` and `alarmStandDown.ts`.
//
// ⚠ VISIBILITY IS BUILT FROM VALUES THAT ARE ALREADY SHARED, NOT FROM A NEW
// LIST. "Now would show a low-side control" is: a live low-side episode that
// is active, not silenced, not in a declared window, not stood down. That is
// `episode.audioOn` — `audioOnFor(...)` in `alarmStandDown.ts`, the SAME
// derived field the audio bridge's `audibleAlarmLevel` gates on — plus the
// treating screen's own mount gate (`treatingSurfaceMounted`, lifted in
// cycle 451) and the level. Nothing here re-derives silenced / snoozed /
// stood-down from raw fields; if `audioOn` changes meaning, this follows.
//
// ⚠ WHY NOT LIFT `NowScreen`'s SURFACE CASCADE INSTEAD. Now picks its surface
// through five inline gates (treating → lead → takeover → banners → floor).
// Two of them are pinned AS SOURCE TEXT by alarm-path guards
// (`readingUnknown.test.ts` requires the takeover predicate to read
// `episode.` directly; `audioLeadScreen.test.ts` requires the lead condition
// to spell its own terms). Lifting them would mean editing those guards,
// which this codebase does not do to an alarm-path test. So the ACTION set
// below mirrors the cascade, and `compactAlarmControl.test.ts` renders the
// REAL `NowScreen` for every state and proves the two offer the same
// controls. The mirror is guarded rather than trusted.
//
// ⚠ CYCLE 454 (operator decision) REPLACES "LOW SIDE ONLY — NO COMPACT
// CONTROL FOR A HIGH, EVER". High and Urgent High now get the same off-Now
// banner as the low side — same component, slot and page rules — carrying
// Stop only (highs are Stop-only, cycle 454). What stays low-side only: the
// 451.1 route pin and one-shot navigation (`alarmAudible.ts`; highs have no
// sustained audio to pin on), the Log overlay's dock (`lowSideControlOwed`;
// Log opens over sustained audio only), and `alarmSurfaceNeedsScreen`'s
// low-side term (a high's `audioOn` already covers it).
//
// ⚠ NO PREVIEW TERM, ON PURPOSE. A Settings test-alarm builds its OWN
// synthetic episode inside `PreviewOverlay`; the shell's real episode never
// sees it, so a preview cannot make this control appear. And a preview must
// never be able to HIDE a real control — 451.1's rule for the audio gate,
// applied here in the same direction. (`PreviewOverlay` also cancels any
// preview the instant the real episode leaves idle.)

import { treatingSurfaceMounted } from "./treating";

/** The episode fields this module may read. A structural subset of
 *  `AlarmEpisodeState`, so a test can build one without a hook. */
export interface AlarmControlEpisode {
  isLive: boolean;
  episodeLevel: string | null;
  phase: string;
  /** `audioOnFor(...)`: active && !silenced && !treating && !standDown. */
  audioOn: boolean;
  treating: boolean;
  quietWindow: boolean;
  bannerSuppressedInRange: boolean;
  hasSnoozed: boolean;
  stopped: boolean;
  audioLead: boolean;
  /** Cycle 410: audio is currently sounding for this member (the lead
   *  window's descent). The lead screen shows Snooze iff this. */
  audible: boolean;
  checkinPending: boolean;
  ackFailure: unknown | null;
}

/** The low-side level a control is owed for, or null.
 *
 *  ⚠ `ackFailure` KEEPS IT UP. A Snooze or Stop that failed on a NETWORK
 *  fault keeps its optimistic silence (cycle 409's rule), so `audioOn` goes
 *  false — and if that alone decided visibility, the control would vanish
 *  the moment its own action failed, taking the failure notice with it.
 *  That is the cycle-416 defect ("the operator believed six times that he
 *  had stopped an alarm"). Now keeps its surface up on a failed ack; so
 *  does this. */
export function lowSideControlOwed(
  e: AlarmControlEpisode,
): "low" | "urgent_low" | null {
  if (!e.isLive) return null;
  const level = e.episodeLevel;
  if (level !== "low" && level !== "urgent_low") return null;
  if (e.bannerSuppressedInRange) return null;
  if (treatingSurfaceMounted(e)) return null;
  if (!e.audioOn && e.ackFailure == null) return null;
  return level;
}

export type AlarmControlLevel = "low" | "urgent_low" | "high" | "urgent_high";

/** Cycle 454: the level the off-Now banner is owed for, on EITHER side.
 *  The low side is `lowSideControlOwed`, unchanged. A high is owed on the
 *  same terms — live, not stood down in range, and sounding (`audioOn`, the
 *  field the audio bridge gates on) OR carrying a failed ack (cycle 416: the
 *  banner must not vanish with its own failure). A Stopped high is not owed
 *  off Now, exactly as a Stopped low is not; Now's banner shows "STOPPED". */
export function alarmControlOwed(
  e: AlarmControlEpisode,
): AlarmControlLevel | null {
  const low = lowSideControlOwed(e);
  if (low != null) return low;
  if (!e.isLive) return null;
  const level = e.episodeLevel;
  if (level !== "high" && level !== "urgent_high") return null;
  if (e.bannerSuppressedInRange) return null;
  if (!e.audioOn && e.ackFailure == null) return null;
  return level;
}

/** `pathname` is `p`, or `p` followed by `/` or `-`.
 *
 *  The same prefix idiom as `routeReturnExempt` in `alarmAudible.ts`
 *  (`/pair` carries `/pair-device`, `/pair-approve` and a trailing slash;
 *  `/pairings` is NOT pairing). That function is 451.1's pin and is not
 *  touched this cycle; `compactAlarmControl.test.ts` proves the two agree on
 *  the pairing family so they cannot drift apart. */
export function pathInFamily(pathname: string, p: string): boolean {
  return (
    pathname === p ||
    pathname.startsWith(p + "/") ||
    pathname.startsWith(p + "-")
  );
}

/** The pairing family: exempt from 451.1's pin, and the one place the compact
 *  control must never navigate away from. */
export function isPairingPath(pathname: string): boolean {
  return pathInFamily(pathname, "/pair");
}

/** The routes that render the pairing CODE screen (`KioskPairingScreen`):
 *  `/pair` and `/pair-device`. On the Android-tablet kiosk that screen is a
 *  full-stage takeover (`position: absolute; inset: 0`), so an in-flow strip
 *  would sit under it and the control must float instead. `/pair-approve`
 *  is the code ENTRY form, laid out in the frame like any other screen. */
export function isPairingDisplayPath(pathname: string): boolean {
  return isPairingPath(pathname) && !pathInFamily(pathname, "/pair-approve");
}

/** `/now` is where the full alarm surfaces mount. React Router tolerates a
 *  trailing slash, so `/now/` mounts `NowScreen` too. */
export function isNowPath(pathname: string): boolean {
  return pathname === "/now" || pathname === "/now/";
}

/** The shell mount's decision: owed, and the full surfaces are NOT on screen.
 *
 *  Never on `/now` (the full surfaces are there — no duplicate controls; the
 *  Log overlay docks its own copy, decided by `lowSideControlOwed` alone).
 *  Never on `/login`, which is not an app screen (the authenticated shell
 *  only hosts it for the connect-a-CGM step). */
/** Cycle 453.2: an alarm surface that fills Now's screen with controls a
 *  caregiver needs is up — alarm audio sounding, the lead window, the
 *  treating / quiet-window screen (its Yes/No, "I need help now", "Resume
 *  alerts"), or a low-side control owed (which covers a failed ack). An
 *  overlay that would sit over those controls (the CGM credentials modal)
 *  steps aside while this is true — and ONLY while it is: a Stopped high on
 *  a dark feed stays "live" for good, and the modal is the one sign that
 *  the CGM login was refused. */
export function alarmSurfaceNeedsScreen(e: AlarmControlEpisode): boolean {
  return (
    e.audioOn ||
    (e.isLive && e.audioLead) ||
    treatingSurfaceMounted(e) ||
    lowSideControlOwed(e) != null ||
    // A failed ack on ANY level keeps Now's takeover / Retry up (cycle 416).
    (e.isLive && e.ackFailure != null)
  );
}

export function compactAlarmControlVisible(s: {
  episode: AlarmControlEpisode;
  pathname: string;
}): boolean {
  if (alarmControlOwed(s.episode) == null) return false;
  if (isNowPath(s.pathname)) return false;
  if (pathInFamily(s.pathname, "/login")) return false;
  return true;
}

/** Which of Now's surfaces would be carrying this episode's controls. */
export type NowLowSideSurface = "lead" | "takeover" | "urgentBanner" | "lowBanner";
/** Cycle 454: Now's `HighAlarmBanner`, which carries both high levels. */
export type NowAlarmSurface = NowLowSideSurface | "highBanner";

export interface CompactAlarmActions {
  level: AlarmControlLevel;
  surface: NowAlarmSurface;
  /** `episode.address()` — every surface's Snooze. */
  snooze: boolean;
  /** `episode.stop` — `LowAlarmBanner`'s Stop. Never on urgent low. */
  stop: boolean;
  /** `episode.soundNow()` — the lead screen's "Sound the alarm now". */
  soundNow: boolean;
  /** The surface carries an action whose handler lives in `NowScreen` ("I'm
   *  treating this", "I've got <name>", the custody question). Offered as a
   *  "Go to alarm" button — false on the pairing family, which never
   *  navigates. */
  goToAlarm: boolean;
  /** A failed ack: `AckFailureNotice` with its Retry (`episode.retryAck`). */
  retry: boolean;
}

/** What the compact control offers: the actions the matching Now surface
 *  offers, no more and no fewer.
 *
 *  ⚠ THE CASCADE BELOW MIRRORS `NowScreen`, TERM FOR TERM, AND A TEST HOLDS
 *  IT THERE. Read beside NowScreen's lead gate, `urgentCollapsed`,
 *  `showActiveButtons`, and the `LowAlarmBanner` / `UrgentBanner` state
 *  machines. The parity test renders the real screen per state; if either
 *  side moves alone, it goes red. */
export function compactAlarmActions(
  e: AlarmControlEpisode,
  opts: { canNavigate: boolean },
): CompactAlarmActions | null {
  const level = alarmControlOwed(e);
  if (level == null) return null;
  const retry = e.ackFailure != null;

  if (level === "high" || level === "urgent_high") {
    // Cycle 454: `HighAlarmBanner`, both high levels. Stop only — never
    // Snooze (operator decision) — plus the failure notice's Retry.
    return {
      level,
      surface: "highBanner",
      snooze: false,
      stop: true,
      soundNow: false,
      goToAlarm: false,
      retry,
    };
  }

  if (level === "low") {
    // `LowAlarmBanner`: a failed ack forces `showActive`; snoozed/stopped
    // are not reachable here (they make `audioOn` false and, with no
    // failure, `lowSideControlOwed` null). Snooze + Stop.
    return {
      level,
      surface: "lowBanner",
      snooze: true,
      stop: true,
      soundNow: false,
      goToAlarm: false,
      retry,
    };
  }

  // Urgent low. NowScreen's lead gate (cycle 410), minus the terms
  // `lowSideControlOwed` already guarantees (live, level, stood down, and the
  // treating/quiet mount that wins above it).
  const lead =
    e.audioLead &&
    !e.treating &&
    !e.quietWindow &&
    !e.hasSnoozed &&
    e.phase !== "snoozed";
  if (lead) {
    return {
      level,
      surface: "lead",
      // "Snooze if and only if audio is sounding" — the descent.
      snooze: e.audible,
      stop: false,
      soundNow: true,
      // "I've got <name>" is NowScreen's `beginTreating`.
      goToAlarm: opts.canNavigate,
      retry,
    };
  }

  // NowScreen's `urgentCollapsed`: a failed ack keeps the takeover open.
  const collapsed =
    e.ackFailure == null &&
    (e.hasSnoozed || e.phase === "snoozed" || e.stopped || e.treating);
  if (collapsed) {
    // `UrgentBanner`'s re-armed branch: Snooze only (cycle 73).
    return {
      level,
      surface: "urgentBanner",
      snooze: e.phase === "active" && e.hasSnoozed && !e.stopped,
      stop: false,
      soundNow: false,
      goToAlarm: false,
      retry,
    };
  }

  // The takeover. `showActiveButtons` = Snooze + "I'm treating this"; the
  // custody question (cycle 447) mounts on it while a check-in is pending.
  const showActive = e.phase === "active" && !e.hasSnoozed && !e.stopped;
  return {
    level,
    surface: "takeover",
    snooze: showActive,
    stop: false,
    soundNow: false,
    goToAlarm: opts.canNavigate && (showActive || e.checkinPending),
    retry,
  };
}
